import { getCarsByPage } from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors'
import { useCarRentalServiceStore } from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { useCheckoutContext } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQueryKey, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import {
  CarFilters,
  CarFilterType,
  FilterableCars,
} from '@/scopes/checkout/filterableCars/domain/types'
import { filterableCarsAPIClient } from '@/scopes/checkout/filterableCars/infrastructure/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import * as React from 'react'

export function useGetAllCarsQueryKey() {
  const sid = useCheckoutSid()

  return React.useMemo(() => ['filterable-cars-get-all-cars', sid], [sid])
}

export function useGetAllCarsQuery() {
  const sid = useCheckoutSid()
  const queryKey = useGetAllCarsQueryKey()
  const { contextItemId } = useCheckoutContext()

  return useQuery<FilterableCars>({
    queryKey,
    queryFn: async () =>
      filterableCarsAPIClient.getAllCars({
        sid,
        contextItemId,
      }),
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
  })
}

function updateFilterInFilterableCars<K extends CarFilterType>(carFilterType: K, filter: CarFilters[K]) {
  return produce((currentFilterableCars: FilterableCars) => {
    currentFilterableCars.filters[carFilterType] = filter
  })
}

export function useFilter<K extends CarFilterType>(carFilterType: K) {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useGetAllCarsQueryKey()

  const { isLoading, isFetching, data } = useGetAllCarsQuery()
  const filters = data?.filters ?? {}
  const filter = (filters as CarFilters)[carFilterType] ?? null

  const setFilterMutation = useMutation({
    mutationFn: async (updatedFilter: CarFilters[K]) => {
      await filterableCarsAPIClient.setCarFilterSelection({ sid, contextItemId }, carFilterType, updatedFilter)
    },
    onMutate: async (updatedFilter: CarFilters[K]) => {
      await queryClient.cancelQueries({ queryKey })
      const previousBookingDraft = queryClient.getQueryData(queryKey)

      queryClient.setQueryData(queryKey, updateFilterInFilterableCars(carFilterType, updatedFilter))

      return { previousBookingDraft }
    },
    onError: async (_, __, context) => {
      if (!context) {
        return
      }
      queryClient.setQueryData(queryKey, context.previousBookingDraft)
      await queryClient.invalidateQueries({ queryKey })
    },
    onSuccess: async () => {
      if (!filter) {
        return
      }
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    isLoading,
    isFetching,
    filter,
    setFilterValue: setFilterMutation.mutate,
  }
}

export function useSelectCar() {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const selectCarMutation = useMutation({
    mutationFn: async (carCode: string) => {
      await filterableCarsAPIClient.setSelectFilteredCar({ sid, contextItemId }, carCode)
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey })
      const previousBookingDraft = queryClient.getQueryData(queryKey)

      return { previousBookingDraft }
    },
    onError: async (_, __, context) => {
      if (!context) {
        return
      }
      queryClient.setQueryData(queryKey, context.previousBookingDraft)
      await queryClient.invalidateQueries({ queryKey })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    selectCar: selectCarMutation.mutate,
  }
}

export function useFilterableCarsAvailable() {
  const { isLoading, data } = useGetAllCarsQuery()
  const { filters, cars } = data ?? {}
  const { filterableCarsPagination, goToPreviousPage, goToNextPage, goToPage, setTotalPages } = useCarRentalServiceStore()
  const { currentPage, isLastPage, isFirstPage, totalPages } = filterableCarsPagination

  const carsPerPage = 24
  const totalCars = cars ? Object.keys(cars).length : 0

  const getPaginatedCars = React.useCallback(() => {
    return getCarsByPage(cars, carsPerPage, currentPage)
  }, [cars, carsPerPage, currentPage])

  React.useEffect(() => {
    setTotalPages(Math.ceil(totalCars / carsPerPage))
  }, [totalCars, setTotalPages])

  return {
    cars,
    filters,
    isLoading,
    totalPages,
    totalCars,
    currentPage,
    paginatedCars: getPaginatedCars(),
    isFirstPage,
    isLastPage,
    goToNextPage,
    goToPreviousPage,
    goToPage,
  }
}
